import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Minus } from "lucide-react";
import { Reveal } from "../Reveal";
import { useFaqs } from "@/lib/usePublicContent";
import type { Lang } from "@/i18n";
import { Breadcrumbs } from "../Breadcrumbs";
import { RelatedLinks } from "../RelatedLinks";

export function FaqPage({ lang }: { lang: Lang }) {
  const { t } = useTranslation();
  const fallback = t("faq.items", { returnObjects: true }) as { q: string; a: string }[];
  const { data: dbFaqs } = useFaqs(lang);
  const items =
    dbFaqs && dbFaqs.length > 0 ? dbFaqs.map((f) => ({ q: f.q, a: f.a })) : fallback;
  const [open, setOpen] = useState<number | null>(0);
  const glossary = t("faq.glossary", { returnObjects: true }) as { term: string; def: string }[];

  return (
    <>
      <Breadcrumbs lang={lang} current="faq" />
      <section className="relative overflow-hidden border-b border-[var(--border-strong)]">
        <div className="grain" aria-hidden />
        <div className="container-page relative py-20 md:py-28">
          <Reveal>
            <span className="eyebrow">— FAQ</span>
            <h1 className="font-display mt-4 text-5xl font-bold leading-[0.98] tracking-[-0.04em] md:text-7xl">
              {t("faq.h1")}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="section">
        <div className="container-page max-w-3xl">
          <ul className="border-t border-foreground">
            {items.map((it, i) => {
              const isOpen = open === i;
              return (
                <li key={i} className="border-b border-[var(--border-strong)]">
                  <Reveal delay={i * 0.03}>
                    <button
                      onClick={() => setOpen(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      className="flex w-full items-center justify-between gap-6 py-6 text-left"
                    >
                      <span className="font-display text-lg font-medium tracking-[-0.01em] text-foreground md:text-xl">
                        {it.q}
                      </span>
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-foreground text-foreground">
                        {isOpen ? <Minus size={14} /> : <Plus size={14} />}
                      </span>
                    </button>
                    <div
                      className={`grid overflow-hidden transition-all duration-300 ${
                        isOpen
                          ? "grid-rows-[1fr] pb-7 opacity-100"
                          : "grid-rows-[0fr] pb-0 opacity-0"
                      }`}
                    >
                      <div className="min-h-0">
                        <p className="max-w-2xl text-[15px] leading-relaxed text-foreground/75">
                          {it.a}
                        </p>
                      </div>
                    </div>
                  </Reveal>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section className="section border-t border-[var(--border-strong)] bg-[var(--surface)]">
        <div className="container-page max-w-3xl">
          <Reveal>
            <span className="eyebrow">— Glossary</span>
            <h2 className="font-display mt-4 text-3xl font-bold leading-[1.05] tracking-[-0.03em] md:text-4xl">
              {t("faq.glossaryTitle")}
            </h2>
          </Reveal>
          <dl className="mt-10 space-y-6">
            {glossary.map((g, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <div className="border-l-2 border-foreground/30 pl-5">
                  <dt className="font-display text-lg font-semibold tracking-[-0.01em] text-foreground">
                    {g.term}
                  </dt>
                  <dd className="mt-1 text-[15px] leading-relaxed text-foreground/75">{g.def}</dd>
                </div>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>
      <RelatedLinks lang={lang} items={["services", "tools", "about"]} />
    </>
  );
}
